import React, { useState, useRef, useEffect } from 'react';
import './CommandPage.css';
import TextInput from '../components/TextInput';
import VoiceInput from '../components/VoiceInput';
import ApiService from '../services/ApiService';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'system';
  timestamp: Date;
  intent?: string;
}

interface CommandPageProps {
  onLexResponse?: (response: any) => void;
}

const CommandPage: React.FC<CommandPageProps> = ({ onLexResponse }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputSectionRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Check for hash in URL and scroll to input section if needed
  useEffect(() => {
    if (window.location.hash === '#input-section' && inputSectionRef.current) {
      // Scroll to input section with a slight delay to ensure rendering is complete
      setTimeout(() => {
        inputSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        // Focus the text input if possible
        const textInput = document.querySelector('.text-input') as HTMLInputElement;
        if (textInput) {
          textInput.focus();
        }
      }, 100);
    }
  }, []);
  
  // Process input from text or voice
  const processInput = async (text: string, isVoice: boolean = false) => {
    setIsProcessing(true);
    
    // Add user message
    const userMessage: Message = {
      id: `msg-${Date.now()}-user`,
      text,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage]);
    
    try {
      // Send to API service
      const response = await ApiService.sendCommand(text);
      
      // Call the onLexResponse callback if provided
      if (onLexResponse) {
        onLexResponse(response);
      }
      
      // Extract response message
      const responseText = response.response || `I received your ${isVoice ? 'voice' : 'text'} command but couldn't process it.`;
      
      // Extract intent if available
      const intent = response.intent || 'Unknown';
      
      // Add system message
      const systemMessage: Message = {
        id: `msg-${Date.now()}-system`,
        text: responseText,
        sender: 'system',
        timestamp: new Date(),
        intent
      };
      
      setMessages(prevMessages => [...prevMessages, systemMessage]);
      
      // If this was a voice command, speak the response
      if (isVoice && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(responseText);
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Error processing input:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: `msg-${Date.now()}-system`,
        text: 'Sorry, I encountered an error processing your request. Please try again.',
        sender: 'system',
        timestamp: new Date()
      };
      
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle text input submission
  const handleTextSubmit = (text: string) => {
    processInput(text);
  };
  
  // Handle voice input result
  const handleVoiceResult = (text: string) => {
    processInput(text, true);
  };
  
  // Get CSS class for message based on intent
  const getMessageClass = (message: Message): string => {
    if (message.sender === 'user') return 'user-message';
    
    // Add intent-specific classes for system messages
    if (message.intent) {
      switch (message.intent) {
        case 'ControlDevice':
          return 'system-message device-message';
        case 'WeatherIntent':
          return 'system-message weather-message';
        case 'TimerIntent':
          return 'system-message timer-message';
        default:
          return 'system-message';
      }
    }
    
    return 'system-message';
  };
  
  return (
    <div className="command-page">
      {/* Input section at the top */}
      <div id="input-section" ref={inputSectionRef} className="input-section">
        <TextInput 
          onSubmit={handleTextSubmit} 
          placeholder="Enter a command like 'Turn on the lights'"
          disabled={isProcessing}
        />
        <VoiceInput 
          onResult={handleVoiceResult}
          disabled={isProcessing}
        />
      </div>
      
      {/* Messages container below */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>Try saying or typing a command like:</p>
            <ul>
              <li>"Turn on the living room lights"</li>
              <li>"What's the weather today?"</li>
              <li>"Set a timer for 5 minutes"</li>
            </ul>
          </div>
        ) : (
          messages.map(message => (
            <div 
              key={message.id} 
              className={`message ${getMessageClass(message)}`}
            >
              <div className="message-content">{message.text}</div>
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default CommandPage;